import React, { useState } from 'react';
import { Avatar, Button, Card, CardContent, IconButton, Tooltip } from '@mui/material';
import ZoomInIcon from '@mui/icons-material/ZoomIn';
import CameraAltIcon from '@mui/icons-material/CameraAlt';
import ThreeSixtyIcon from '@mui/icons-material/ThreeSixty';
import { GroupedInventoryItem, ItemDTO } from '../types/Inventory';
import { useCurrency } from '../../../../hooks/useCurrency';
import { RARITY_COLORS, RarityGrade } from '../../../utils/RarityColors';
import { moneyFromMinor } from '../../../utils/UnitConversion.utils';
import { formatSteamImageUrl, useSteamImageUrl } from '../../../../hooks/useSteamImageUrl';
import ItemScreenshotModal from '../../../items/ItemScreenshotModal';
import Item3DViewerModal from '../../../items/Item3DViewerModal';

interface InventoryItemCardProps {
  group: GroupedInventoryItem;
  selected: boolean;
  onToggle: (group: GroupedInventoryItem) => void;
  currencySymbol: string;
  onOpenDetails: (group: GroupedInventoryItem) => void;
}

const PHOTO_ALLOWED_TYPES = ['PISTOL', 'SMG', 'RIFLE', 'SNIPER_RIFLE', 'MACHINE_GUN', 'SHOTGUN', 'EQUIPMENT', 'KNIFE', 'GLOVES'];

const InventoryItemCard: React.FC<InventoryItemCardProps> = ({
                                                               group,
                                                               selected,
                                                               onToggle,
                                                               currencySymbol,
                                                               onOpenDetails
                                                             }) => {
  const { formatAmount } = useCurrency();
  const item = group.representative;
  const rarityColor = RARITY_COLORS[item.details?.rarity as RarityGrade] ?? '#ffffff';

  const [screenshotOpen, setScreenshotOpen] = useState(false);
  const [viewer3DOpen, setViewer3DOpen] = useState(false);

  const rawImg = item.catalog?.imageLink ?? undefined;
  const imgSrc = useSteamImageUrl(rawImg, '360fx360f');

  const handleCardClick = (e: React.MouseEvent) => {
    onOpenDetails(group);
  };

  const handleContextMenu = (e: React.MouseEvent) => {
    e.preventDefault();
    onOpenDetails(group);
  };

  const handleInspectIngame = (e: React.MouseEvent) => {
    e.stopPropagation();
    if (item.details?.ingameLink) {
      window.location.href = item.details.ingameLink;
    }
  };

  const handleScreenshot = (e: React.MouseEvent) => {
    e.stopPropagation();
    setScreenshotOpen(true);
  };

  const handle3DViewer = (e: React.MouseEvent) => {
    e.stopPropagation();
    setViewer3DOpen(true);
  };

  const renderAttachments = (item: ItemDTO) => {
    const attachments = item.attachments || [];
    if (attachments.length === 0) return null;
    return (
        <div className="absolute bottom-1 left-1 flex flex-col-reverse gap-0 z-20 backdrop-blur-[1px] rounded-md">
          {attachments.map((att, idx) => (
              <div key={idx} className="-mb-1 first:mb-0">
                <Tooltip title={att.marketHashName} arrow placement="right">
                  <div className="relative group/sticker">
                    <img
                        src={formatSteamImageUrl(att.imageLink, '100fx100f')}
                        alt="sticker"
                        className="w-7 h-7 object-contain transition-transform duration-200 group-hover/sticker:scale-[1.8] group-hover/sticker:translate-x-1 drop-shadow-md"
                    />
                  </div>
                </Tooltip>
              </div>
          ))}
        </div>
    );
  };

  const contentFadeClass = selected ? 'opacity-40 grayscale-[0.4] transition-all duration-300' : 'transition-all duration-300';

  // Extract raw value and pull up to 0.02 limit if it is below 2 minor units
  const rawPriceFromMinor = moneyFromMinor(item.catalog?.priceRealMinor ?? 0);
  const finalPriceAmount = rawPriceFromMinor < 0.02 ? 0.02 : rawPriceFromMinor;

  const itemGroup = item.details?.itemGroup || (item.catalog as any)?.itemGroup;

  return (
      <>
        <Card
            onClick={handleCardClick}
            onContextMenu={handleContextMenu}
            className="relative bg-[#1c1f26] border-4 border-transparent transition-all duration-300 cursor-pointer"
        >
          <CardContent className="flex flex-col !p-3">
            {group.totalCount > 1 && <div className={`absolute top-2 right-2 bg-black/70 text-xs text-white px-1.5 py-0.5 rounded z-30 ${contentFadeClass}`}>x{group.totalCount}</div>}

            <div className={`h-[36px] ${contentFadeClass}`}>
              <div className="text-white font-semibold text-[0.80rem] leading-[1.1] line-clamp-2">
                {item.catalog?.groupName ?? 'Unknown Item'}</div>
              <div className="flex items-center gap-1.5">
                {item.details?.isSouvenir && <span className="text-yellow-400 font-semibold text-[0.7rem] tracking-[0.3px]">Souvenir</span>}
                {item.details?.isStattrak && <span className="text-orange-400 font-semibold text-[0.7rem] tracking-[0.3px]">StatTrak™</span>}
                {item.catalog?.wearName && <span className="text-gray-300 text-[0.7rem] font-medium tracking-[0.2px] capitalize">{item.catalog.wearName.replace('_', ' ').toLowerCase()}{item?.details?.phase && <> ({item.details.phase})</>}</span>}
              </div>
            </div>

            <div className={`relative flex justify-center items-end mb-3 rounded-md overflow-hidden group/img ${contentFadeClass}`} style={{ background: `linear-gradient(to top, ${rarityColor} 0%, transparent 100%)`, height: '160px' }}>
              {imgSrc ? <img src={imgSrc} alt="item" className="w-44 h-44 object-contain" style={{ objectPosition: 'center 80%' }} /> : <Avatar sx={{ width: 80, height: 80 }} />}
              {renderAttachments(item)}
              <div className="absolute bottom-2 right-2 flex flex-col gap-1.5 z-20">
                <Tooltip title="Inspect In-Game" arrow placement="left">
                  <IconButton size="small" onClick={handleInspectIngame} className="!bg-neutral-800 hover:!bg-neutral-700 !text-white !rounded-full border border-neutral-700/50 !opacity-60 hover:!opacity-100 transition-opacity"><ZoomInIcon sx={{ fontSize: 20 }} /></IconButton>
                </Tooltip>
                {itemGroup && PHOTO_ALLOWED_TYPES.includes(itemGroup) && (
                    <>
                      <Tooltip title="View Screenshot" arrow placement="left">
                        <IconButton size="small" onClick={handleScreenshot} className="!bg-neutral-800 hover:!bg-neutral-700 !text-white !rounded-full border border-neutral-700/50 !opacity-60 hover:!opacity-100 transition-opacity"><CameraAltIcon sx={{ fontSize: 20 }} /></IconButton>
                      </Tooltip>
                      <Tooltip title="360° 3D Viewer" arrow placement="left">
                        <IconButton size="small" onClick={handle3DViewer} className="!bg-neutral-800 hover:!bg-neutral-700 !text-white !rounded-full border border-neutral-700/50 !opacity-60 hover:!opacity-100 transition-opacity"><ThreeSixtyIcon sx={{ fontSize: 20 }} /></IconButton>
                      </Tooltip>
                    </>
                )}
              </div>
            </div>

            <div className={`flex text-white text-start font-semibold mb-2 ${contentFadeClass}`}>
              <div className="mr-1 text-gray-400">{currencySymbol}</div>
              <div>{formatAmount(finalPriceAmount)}</div>
            </div>

            <div className={`h-[40px] w-full ${contentFadeClass}`}>
              {item.details?.floatValue != null && (
                  <>
                    <div className="relative h-[8px] rounded-full w-[70%]" style={{ background: 'linear-gradient(to right, #14532d 0%, #14532d 7%, #22c55e 7%, #22c55e 15%, #eab308 15%, #eab308 38%, #f87171 38%, #f87171 45%, #b91c1c 45%, #b91c1c 100%)' }}>
                      <div className="absolute top-1/2 -translate-y-1/2 w-[2px] h-[12px] bg-white rounded-sm shadow" style={{ left: `${Math.min(Math.max(item.details.floatValue * 100, 0), 100)}%` }} />
                    </div>
                    <div className="flex justify-between text-[0.72rem] text-gray-400 mt-1.5">
                      <div>{item.details.floatValue.toFixed(10)}</div>
                      <div>{item.details.paintSeed != null ? `#${item.details.paintSeed}` : ''}</div>
                    </div>
                  </>
              )}
            </div>

            <Button
                fullWidth
                variant="contained"
                className="mt-auto group"
                onClick={(e) => {
                  e.stopPropagation();
                  onToggle(group);
                }}
                sx={{
                  height: 36,
                  backgroundColor: selected ? 'rgba(115, 115, 115, 0.2)' : undefined,
                  color: selected ? '#a3a3a3' : undefined,
                  '&:hover': selected ? {
                    backgroundColor: '#d32f2f !important',
                    color: '#ffffff !important'
                  } : undefined
                }}
            >
              {selected ? 'Deselect' : 'Sell Item'}
            </Button>
          </CardContent>
        </Card>

        <ItemScreenshotModal
            open={screenshotOpen}
            assetId={item.itemAssetId}
            onClose={() => setScreenshotOpen(false)}
        />

        <Item3DViewerModal
            open={viewer3DOpen}
            assetId={item.itemAssetId}
            onClose={() => setViewer3DOpen(false)}
        />
      </>
  );
};

export default InventoryItemCard;
